import type { ChatMessage } from '../types';

const STORAGE_VERSION = 1 as const;
const MAX_MESSAGES_PER_CONVERSATION = 200;

export interface PersistedChatBlob {
  version: typeof STORAGE_VERSION;
  messagesByConversation: Record<string, ChatMessage[]>;
  /** conversationId -> peerUserId -> last read message id from that peer */
  readReceipts: Record<string, Record<string, string>>;
  pinnedConversationIds: string[];
  starredMessageIds: string[];
  favoriteConversationIds: string[];
}

const keyForTenant = (tenantKey: string) => `coremonit_team_chat_v${STORAGE_VERSION}_${tenantKey}`;

export function loadChatBlob(tenantKey: string): PersistedChatBlob {
  try {
    const raw = localStorage.getItem(keyForTenant(tenantKey));
    if (!raw) return emptyBlob();
    const parsed = JSON.parse(raw) as PersistedChatBlob;
    if (parsed?.version !== STORAGE_VERSION) return emptyBlob();
    return {
      ...emptyBlob(),
      ...parsed,
      messagesByConversation: parsed.messagesByConversation || {},
      readReceipts: parsed.readReceipts || {},
      pinnedConversationIds: parsed.pinnedConversationIds || [],
      starredMessageIds: parsed.starredMessageIds || [],
      favoriteConversationIds: parsed.favoriteConversationIds || [],
    };
  } catch {
    return emptyBlob();
  }
}

export function emptyBlob(): PersistedChatBlob {
  return {
    version: STORAGE_VERSION,
    messagesByConversation: {},
    readReceipts: {},
    pinnedConversationIds: [],
    starredMessageIds: [],
    favoriteConversationIds: [],
  };
}

export function saveChatBlob(tenantKey: string, blob: PersistedChatBlob): void {
  const trimmed: PersistedChatBlob = {
    ...blob,
    messagesByConversation: {},
  };
  for (const [cid, list] of Object.entries(blob.messagesByConversation)) {
    trimmed.messagesByConversation[cid] = list
      .slice(-MAX_MESSAGES_PER_CONVERSATION)
      .map((m) => ({
        ...m,
        pending: false,
      }));
  }
  try {
    localStorage.setItem(keyForTenant(tenantKey), JSON.stringify(trimmed));
  } catch (e) {
    console.warn('[team-chat] persist failed (quota?)', e);
  }
}
